/**
 * Utility functions for user avatar/profile picture handling
 * Ensures consistent avatar display across the entire application
 */

export interface UserAvatarSource {
  avatar?: string;
  profile_picture?: string;
  first_name?: string;
  last_name?: string;
  username?: string;
  id?: string | number;
  user_id?: string | number;
}

/**
 * Get user avatar from multiple sources with fallback
 * Prioritizes user object data over localStorage to ensure each user has their own avatar
 */
export const getUserAvatar = (user: UserAvatarSource | null | undefined): string | null => {
  if (!user) return null;
  
  // PRIORITY 1: Get avatar directly from user object (from backend/API)
  // This ensures each user gets their own avatar from the database
  if (user.avatar) {
    return user.avatar;
  }
  
  if (user.profile_picture) {
    return user.profile_picture;
  }
  
  // PRIORITY 2: Get from user-specific localStorage (if user has username/id)
  // Use username or a unique identifier to make localStorage user-specific
  const userIdentifier = (user as any)?.username || (user as any)?.id || (user as any)?.user_id;
  
  if (userIdentifier) {
    // Try user-specific localStorage key
    const userSpecificKey = `userAvatar_${userIdentifier}`;
    const cachedAvatar = localStorage.getItem(userSpecificKey);
    if (cachedAvatar) {
      return cachedAvatar;
    }
    
    // Try user-specific profile
    try {
      const userProfileKey = `userProfile_${userIdentifier}`;
      const profile = JSON.parse(localStorage.getItem(userProfileKey) || '{}');
      if (profile.avatar) {
        return profile.avatar;
      }
    } catch {
      // Ignore parsing errors
    }
  }
  
  // PRIORITY 3: Fallback to global localStorage (for backward compatibility)
  // But only if no user identifier is available
  if (!userIdentifier) {
    const globalAvatar = localStorage.getItem('userAvatar');
    if (globalAvatar) {
      return globalAvatar;
    }
    
    try {
      const profile = JSON.parse(localStorage.getItem('userProfile') || '{}');
      if (profile.avatar) {
        return profile.avatar;
      }
    } catch {
      // Ignore parsing errors
    }
  }
  
  // No avatar found
  return null;
};

/**
 * Get user initials for avatar fallback
 */
export const getUserInitials = (user: UserAvatarSource | null | undefined): string => {
  if (!user) return 'U';
  
  if (user.first_name && user.last_name) {
    return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
  }
  
  if (user.first_name) {
    return user.first_name[0].toUpperCase();
  }
  
  if (user.username) {
    return user.username[0].toUpperCase();
  }
  
  return 'U';
};

/**
 * Store user avatar in localStorage for persistence
 * Uses user-specific keys to ensure each user has their own avatar
 */
export const storeUserAvatar = (avatar: string | null, userIdentifier?: string | number): void => {
  // Use user identifier if provided, otherwise use global key (for backward compatibility)
  if (userIdentifier) {
    const userSpecificKey = `userAvatar_${userIdentifier}`;
    const userProfileKey = `userProfile_${userIdentifier}`;
    
    if (avatar) {
      localStorage.setItem(userSpecificKey, avatar);
      // Also store in user-specific profile object
      try {
        const profile = JSON.parse(localStorage.getItem(userProfileKey) || '{}');
        profile.avatar = avatar;
        localStorage.setItem(userProfileKey, JSON.stringify(profile));
      } catch {
        // If parsing fails, create new object
        localStorage.setItem(userProfileKey, JSON.stringify({ avatar }));
      }
    } else {
      localStorage.removeItem(userSpecificKey);
      try {
        const profile = JSON.parse(localStorage.getItem(userProfileKey) || '{}');
        delete profile.avatar;
        localStorage.setItem(userProfileKey, JSON.stringify(profile));
      } catch {
        // Ignore errors
      }
    }
  } else {
    // Fallback to global storage (for backward compatibility)
    if (avatar) {
      localStorage.setItem('userAvatar', avatar);
      try {
        const profile = JSON.parse(localStorage.getItem('userProfile') || '{}');
        profile.avatar = avatar;
        localStorage.setItem('userProfile', JSON.stringify(profile));
      } catch {
        localStorage.setItem('userProfile', JSON.stringify({ avatar }));
      }
    } else {
      localStorage.removeItem('userAvatar');
      try {
        const profile = JSON.parse(localStorage.getItem('userProfile') || '{}');
        delete profile.avatar;
        localStorage.setItem('userProfile', JSON.stringify(profile));
      } catch {
        // Ignore errors
      }
    }
  }
  
  // Broadcast update to all components
  window.dispatchEvent(new Event('avatarUpdated'));
};

